import type { UserPublic } from '@repo/types/users';

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useGenerateStudentReport } from '@repo/api/report/useGenerateStudentReport';
import { reportErrorMessages } from '@repo/lib/messages/reportErrorMessages';
import { studentReportSchema, type StudentReportSchema } from '@repo/schemas/report/studentReport';
import { format, parseISO } from 'date-fns';
import { DownloadIcon, FileTextIcon } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DatePicker } from '@/components/ui/date-picker';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { LoadingOverlay, LoadingOverlayLabel, LoadingOverlaySpinner } from '@/components/ui/loading-overlay';
import { LoadingSwap } from '@/components/ui/loading-swap';
import { Textarea } from '@/components/ui/textarea';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';
import { saveBlob } from '@/utils/file';

const DAY = 'yyyy-MM-dd';

const EMPTY_FORM: StudentReportSchema = {
  from: null,
  to: null,
  desc: null,
  emailParents: false,
};

/** Used when the BE's Content-Disposition isn't readable cross-origin. */
const localFilename = (student: UserPublic): string => `raport-${student.name}-${student.surname}.pdf`.replace(/\s+/g, '-').toLowerCase();

const emailSummary = (emailedParents: number | null): string | undefined => {
  if (emailedParents === null) return undefined;
  if (emailedParents === 0) return 'Uczeń nie ma przypisanych rodziców';
  return `Wysłano do ${emailedParents} rodzic/ów`;
};

interface GenerateStudentReportProps {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  student: UserPublic | undefined;
}

export default function GenerateStudentReport(generateStudentReportProps: GenerateStudentReportProps) {
  const { open, onOpenChange, student } = generateStudentReportProps;
  const { mutateAsync, isPending } = useGenerateStudentReport();

  const form = useForm<StudentReportSchema>({
    mode: 'onSubmit',
    defaultValues: EMPTY_FORM,
    resolver: standardSchemaResolver(studentReportSchema),
  });

  useEffect(() => {
    if (open) form.reset(EMPTY_FORM);
  }, [open, form]);

  const onClose = () => {
    onOpenChange(false);
  };

  const onSubmit = (data: StudentReportSchema) => {
    if (!student) return;

    mutateAsync({ studentId: student.id, data })
      .then((result) => {
        saveBlob(result.blob, result.filename ?? localFilename(student));
        toast.success('Raport wygenerowany', {
          description: data.emailParents ? emailSummary(result.emailedParents) : undefined,
        });
        onClose();
      })
      .catch((error) => {
        toasterMessageHandler(reportErrorMessages, error);
      });
  };

  const handleOnOpenChange = (value: boolean) => {
    if (!value) onClose();
    else onOpenChange(true);
  };

  return (
    <Dialog open={open} onOpenChange={handleOnOpenChange}>
      <DialogContent className={'sm:max-w-lg'}>
        <Form {...form}>
          <DialogBody>
            <LoadingOverlay isLoading={isPending}>
              <LoadingOverlaySpinner />
              <LoadingOverlayLabel>Trwa generowanie raportu...</LoadingOverlayLabel>
            </LoadingOverlay>
            <DialogHeader className={'border-border-primary bg-card items-center gap-2 border-b'}>
              <FeaturedIcon>
                <FileTextIcon />
              </FeaturedIcon>
              <div className={'flex min-w-0 grow flex-col gap-0.5'}>
                <DialogTitle>Generuj raport</DialogTitle>
                <DialogDescription className={'text-text-tertiary text-xs'}>
                  {student ? `Oceny i frekwencja ucznia: ${student.name} ${student.surname}` : 'Oceny i frekwencja ucznia.'}
                </DialogDescription>
              </div>
            </DialogHeader>
            <form id={'generate-student-report-form'} className={'flex grow flex-col gap-3 overflow-auto p-4'} onSubmit={form.handleSubmit(onSubmit)} noValidate>
              <div className={'grid gap-2 sm:grid-cols-2'}>
                <FormField
                  name={'from'}
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Od</FormLabel>
                      <FormControl>
                        <DatePicker className={'w-full'} selected={field.value ? parseISO(field.value) : null} onSelect={(date) => field.onChange(format(date, DAY))} dateFormat={'dd.MM.yyyy'} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  name={'to'}
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Do</FormLabel>
                      <FormControl>
                        <DatePicker className={'w-full'} selected={field.value ? parseISO(field.value) : null} onSelect={(date) => field.onChange(format(date, DAY))} dateFormat={'dd.MM.yyyy'} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <p className={'text-text-tertiary text-xs'}>Pozostaw daty puste, aby uwzględnić cały okres nauki.</p>
              <FormField
                name={'desc'}
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Komentarz nauczyciela</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                        placeholder={'Opcjonalny komentarz widoczny na raporcie'}
                        maxLength={500}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name={'emailParents'}
                control={form.control}
                render={({ field }) => (
                  <FormItem className={'border-border-primary flex flex-row items-start gap-3 rounded-lg border p-3'}>
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={(checked) => field.onChange(checked === true)} />
                    </FormControl>
                    <div className={'flex flex-col gap-0.5'}>
                      <FormLabel>Wyślij raport rodzicom</FormLabel>
                      <FormDescription className={'text-xs'}>Raport zostanie wysłany mailem do wszystkich rodziców przypisanych do ucznia</FormDescription>
                    </div>
                  </FormItem>
                )}
              />
            </form>
          </DialogBody>
          <DialogFooter className={'border-border-primary dark:bg-card bg-accent border-t'}>
            <Button onClick={onClose} type={'button'} variant={'tertiary'} className={'w-full md:w-fit'}>
              Anuluj
            </Button>
            <Button disabled={isPending || !student} form={'generate-student-report-form'} type={'submit'} variant={'primary'} className={'w-full md:w-fit'}>
              <LoadingSwap isLoading={isPending} className={'flex items-center gap-2'}>
                <DownloadIcon />
                Pobierz PDF
              </LoadingSwap>
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
